/**
 * MyOpenTasksWidget — Dashboard sidebar widget showing the current
 * user's top-5 open tasks (Release 1B).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { CheckSquare, ArrowRight, AlertCircle } from "lucide-react";

const PRIORITY_STYLES = {
  low:    "bg-zinc-100 text-zinc-700",
  normal: "bg-blue-50 text-blue-800",
  high:   "bg-amber-50 text-amber-900",
  urgent: "bg-red-50 text-red-800",
};

export default function MyOpenTasksWidget() {
  const [rows, setRows] = useState(null);   // null = loading

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/tasks", {
          params: { mine: true, open_only: true },
        });
        if (!cancelled) setRows((r.data || []).slice(0, 5));
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (rows && rows.length === 0) return null;   // don't show empty widget

  return (
    <Card
      className="p-4 rounded-lg border border-zinc-200 shadow-none"
      data-testid="dashboard-my-tasks-widget"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CheckSquare className="h-4 w-4 text-zinc-700" />
          <div>
            <div className="text-sm font-semibold text-zinc-950">My open tasks</div>
            <div className="text-2xs text-zinc-500">Top 5 items on your plate</div>
          </div>
        </div>
        <Link
          to="/tasks"
          className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1"
          data-testid="dashboard-my-tasks-see-all"
        >
          See all <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      {!rows ? (
        <div className="text-xs text-zinc-500">Loading…</div>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((t) => {
            const overdue = t.due_at && new Date(t.due_at) < new Date();
            return (
              <li
                key={t.id}
                className="flex items-center gap-2 text-sm border-b border-zinc-100 pb-1.5 last:border-0 last:pb-0"
                data-testid={`dashboard-my-task-${t.id}`}
              >
                <span className={`text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded ${PRIORITY_STYLES[t.priority] || PRIORITY_STYLES.normal}`}>
                  {t.priority}
                </span>
                <Link to="/tasks" className="flex-1 truncate hover:underline text-zinc-900">
                  {t.title}
                </Link>
                {t.due_at && (
                  <span
                    className={`text-2xs shrink-0 ${overdue ? "text-red-600 font-medium" : "text-zinc-500"}`}
                    title={new Date(t.due_at).toLocaleString()}
                  >
                    {overdue && <AlertCircle className="h-3 w-3 inline mr-0.5" />}
                    {new Date(t.due_at).toLocaleDateString()}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
